import { useEffect, useState, useMemo } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { fmtDateTime, relativeTime } from "@/lib/format";
import { Check, Clock, AlertCircle, Filter } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  { id: "low",      label: "Low",      color: "#5C6660", bg: "#F2F4F3" },
  { id: "medium",   label: "Medium",   color: "#2980B9", bg: "#EAF2F8" },
  { id: "high",     label: "High",     color: "#B26B00", bg: "#FFF4E5" },
  { id: "critical", label: "Critical", color: "#C0392B", bg: "#FDEDEC" },
];
const PRI_MAP = Object.fromEntries(PRIORITIES.map((p) => [p.id, p]));

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const params = filter === "overdue" ? "?overdue=true" : filter === "completed" ? "?status=completed" : "?status=pending";
      const { data } = await api.get(`/tasks${params}`);
      setItems(data);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const complete = async (id) => {
    try { await api.patch(`/tasks/${id}`, { status: "completed" }); load(); toast.success("Task completed"); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const now = Date.now();

  return (
    <div data-testid="tasks-page">
      <PageHeader eyebrow="Operations" title="Tasks"
        subtitle="Every action auto-generated by the IPMP workflow engine, plus anything manually added." />

      <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1 mb-5 w-fit">
        {[["open", "Open"], ["overdue", "Overdue"], ["completed", "Completed"]].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`task-filter-${id}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${filter === id ? "bg-[#2A4D3E] text-white" : "text-[#5C6660] hover:bg-[#F7F5F2]"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="space-y-2" data-testid="tasks-list">
          {items.map((t) => {
            const p = PRI_MAP[t.priority] || PRI_MAP.medium;
            const overdue = t.status === "pending" && t.due_at && new Date(t.due_at).getTime() < now;
            return (
              <div key={t.id} className="bg-white border border-[#E8E6E1] rounded-2xl p-4 flex items-center gap-4 hover:shadow-sm transition" data-testid={`task-${t.id}`}>
                <div className="w-1 h-10 rounded-full" style={{ background: p.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#2A332C]">{t.title}</div>
                  {t.next_action && <div className="text-xs text-[#5C6660] mt-0.5">{t.next_action}</div>}
                  <div className="flex items-center gap-2 mt-2 text-[11px]">
                    <span style={{ background: p.bg, color: p.color }} className="px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">{p.label}</span>
                    {t.due_at && (
                      <span className={`flex items-center gap-1 ${overdue ? "text-[#C0392B]" : "text-[#8E9992]"}`}>
                        <Clock size={11} /> {overdue ? "Overdue " : ""}Due {relativeTime(t.due_at)}
                      </span>
                    )}
                    {t.stage_trigger && <span className="text-[#8E9992]">Triggered by {t.stage_trigger.replace(/_/g, " ")}</span>}
                  </div>
                </div>
                {t.status === "pending" && (
                  <button onClick={() => complete(t.id)} data-testid={`complete-${t.id}`}
                    className="border border-[#2A4D3E] text-[#2A4D3E] rounded-full px-4 py-1.5 text-xs font-medium hover:bg-[#E6F0EB] flex items-center gap-1.5 whitespace-nowrap">
                    <Check size={12} /> Complete
                  </button>
                )}
                {t.status === "completed" && (
                  <span className="text-[11px] text-[#2A4D3E] font-semibold uppercase tracking-wider">Done · {relativeTime(t.completed_at)}</span>
                )}
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-10 text-center text-sm text-[#8E9992]">
              No tasks in this view.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
